/**
 * Infrastructure Intelligence — Asset Boundaries  (Step 1 of the 8-step intelligence loop)
 *
 * Register infrastructure asset boundaries: road/railway corridors, construction sites,
 * power-line RoW, slope-instability zones, bridge inspection zones.
 * Each asset carries: asset type, project/line ID, owner/contractor, and monitoring cadence.
 *
 * Features:
 *  - List / Map view toggle
 *  - Add: draw polygon on map → fill metadata → save (vertical = 'infrastructure')
 *  - Edit: full metadata form with optional geometry redraw
 *  - Map view: all boundaries rendered with popup (Edit / Delete)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Popup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  PenTool, PlusCircle, Building2, RefreshCw, Trash2, Pencil,
  Check, Map, List,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { satelliteBoundariesApi } from '../../services/api';

// Fix Leaflet default icons in Vite builds
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface Asset {
  id: string;
  name: string;
  description?: string;
  area_hectares?: number;
  monitoring_enabled: boolean;
  monitoring_frequency: string;
  created_at: string;
  geometry?: { type: 'Polygon'; coordinates: number[][][] };
  metadata?: {
    asset_type?: string;
    project_id?: string;
    owner?: string;
    vertical?: string;
  };
}

interface AssetFormState {
  name: string;
  asset_type: string;
  project_id: string;
  owner: string;
  monitoring_frequency: string;
}

const EMPTY_FORM: AssetFormState = {
  name: '',
  asset_type: 'road_corridor',
  project_id: '',
  owner: '',
  monitoring_frequency: 'weekly',
};

const ASSET_TYPES = [
  { value: 'road_corridor',        label: 'Road / Highway Corridor' },
  { value: 'railway_corridor',     label: 'Railway Corridor' },
  { value: 'power_line_row',       label: 'Power-line Right-of-Way' },
  { value: 'construction_site',    label: 'Construction Site' },
  { value: 'slope_zone',           label: 'Slope Instability Zone' },
  { value: 'bridge_zone',          label: 'Bridge Inspection Zone' },
  { value: 'pipeline_row',         label: 'Pipeline Right-of-Way' },
  { value: 'water_body',           label: 'Water Body / Drainage' },
];

const MONITORING_FREQUENCIES = [
  { value: 'daily',     label: 'Daily' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'on_demand', label: 'On Demand' },
];

const CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// ─── FitBounds helper ─────────────────────────────────────────────────────────

const FitBounds: React.FC<{ assets: Asset[] }> = ({ assets }) => {
  const map = useMap();
  useEffect(() => {
    const coords = assets.flatMap((a) =>
      (a.geometry?.coordinates?.[0] ?? []).map((pt) => [pt[1], pt[0]] as [number, number])
    );
    if (coords.length) map.fitBounds(L.latLngBounds(coords), { padding: [50, 50] });
  }, [assets, map]);
  return null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function assetToForm(a: Asset): AssetFormState {
  return {
    name:                 a.name,
    asset_type:           a.metadata?.asset_type ?? 'road_corridor',
    project_id:           a.metadata?.project_id ?? '',
    owner:                a.metadata?.owner ?? '',
    monitoring_frequency: a.monitoring_frequency,
  };
}

function hasRealGeometry(a: Asset): boolean {
  return (a.geometry?.coordinates?.[0]?.length ?? 0) > 2;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AssetBoundariesPage: React.FC = () => {
  const [assets, setAssets]     = useState<Asset[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  const [showForm,   setShowForm]   = useState(false);
  const [editingId,  setEditingId]  = useState<string | null>(null);
  const [form,       setForm]       = useState<AssetFormState>(EMPTY_FORM);
  const [drawnLayer, setDrawnLayer] = useState<any>(null);

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await satelliteBoundariesApi.getAll();
      const all: Asset[] = res.data?.boundaries ?? [];
      setAssets(all.filter((b) => b.metadata?.vertical === 'infrastructure'));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load asset boundaries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnLayer(null);
    setShowForm(true);
    setViewMode('list');
  };

  const openEdit = (a: Asset) => {
    setEditingId(a.id);
    setForm(assetToForm(a));
    setDrawnLayer(null);
    setShowForm(true);
    setViewMode('list');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnLayer(null);
  };

  const setF = (k: keyof AssetFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  // ── Save (create or update) ───────────────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.project_id.trim()) {
      setError('Asset name and Project/Line ID are required');
      return;
    }
    if (!editingId && !drawnLayer) {
      setError('Please draw the asset boundary on the map first');
      return;
    }
    setSaving(true); setError(null);
    try {
      const metadata = {
        asset_type: form.asset_type,
        project_id: form.project_id.trim(),
        owner:      form.owner.trim(),
        vertical:   'infrastructure',
      };

      if (editingId) {
        await satelliteBoundariesApi.update(editingId, {
          name:                 form.name.trim(),
          description:          `${form.asset_type.replace(/_/g, ' ')} — ${form.project_id}`,
          monitoring_frequency: form.monitoring_frequency,
          metadata,
          ...(drawnLayer ? { geometry: drawnLayer.toGeoJSON().geometry } : {}),
        });
        setSuccess(`Asset "${form.name}" updated`);
      } else {
        await satelliteBoundariesApi.create({
          name:                 form.name.trim(),
          description:          `${form.asset_type.replace(/_/g, ' ')} — ${form.project_id}`,
          geometry:             drawnLayer.toGeoJSON().geometry,
          monitoring_enabled:   true,
          monitoring_frequency: form.monitoring_frequency,
          metadata,
        });
        setSuccess(`Asset "${form.name}" registered`);
      }
      closeForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to save asset boundary');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete asset boundary "${name}"? This cannot be undone.`)) return;
    try {
      await satelliteBoundariesApi.deleteById(id);
      setSuccess(`Asset "${name}" deleted`);
      if (editingId === id) closeForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to delete asset');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const validAssets = assets.filter(hasRealGeometry);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <PenTool className="w-7 h-7 text-blue-500" /> Asset Boundaries
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 1 — Define infrastructure asset boundaries for satellite monitoring
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button
            variant={showForm && !editingId ? 'outline' : 'primary'}
            onClick={showForm && !editingId ? closeForm : openCreate}>
            {showForm && !editingId ? 'Cancel' : <><PlusCircle className="w-4 h-4 mr-1" /> Add Asset</>}
          </Button>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── View toggle (only when form is closed) ── */}
      {!showForm && (
        <div className="flex gap-2">
          <Button size="sm" variant={viewMode === 'list' ? 'primary' : 'outline'} onClick={() => setViewMode('list')}>
            <List className="w-4 h-4 mr-1" /> List
          </Button>
          <Button size="sm" variant={viewMode === 'map' ? 'primary' : 'outline'} onClick={() => setViewMode('map')}>
            <Map className="w-4 h-4 mr-1" /> Map
          </Button>
        </div>
      )}

      {/* ── Create / Edit form ── */}
      {showForm && (
        <Card>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">
            {editingId ? 'Edit Asset Boundary' : 'Register New Asset'}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Left: metadata */}
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Asset Name *</label>
                <input className={CLS} required placeholder="e.g. Lagos–Ibadan Expressway Corridor"
                  value={form.name} onChange={setF('name')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Asset Type</label>
                <select className={CLS} value={form.asset_type} onChange={setF('asset_type')}>
                  {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project / Line ID *</label>
                <input className={CLS} required placeholder="e.g. RD-LIE-001 or PL-ABJ-002"
                  value={form.project_id} onChange={setF('project_id')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner / Contractor</label>
                <input className={CLS} placeholder="e.g. FERMA, Julius Berger, PowerGrid"
                  value={form.owner} onChange={setF('owner')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monitoring Frequency</label>
                <select className={CLS} value={form.monitoring_frequency} onChange={setF('monitoring_frequency')}>
                  {MONITORING_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>

              {/* Geometry hint */}
              {!editingId && !drawnLayer && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300">
                  👉 Draw the asset boundary on the map →
                </div>
              )}
              {!editingId && drawnLayer && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2.5 text-xs text-green-800 dark:text-green-300">
                  <Check className="w-3.5 h-3.5 inline mr-1" />Boundary drawn — fill in metadata and save
                </div>
              )}
              {editingId && !drawnLayer && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2.5 text-xs text-blue-800 dark:text-blue-300">
                  Optionally redraw the boundary on the map to update geometry, or save to update metadata only.
                </div>
              )}
              {editingId && drawnLayer && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2.5 text-xs text-green-800 dark:text-green-300">
                  <Check className="w-3.5 h-3.5 inline mr-1" />New boundary drawn — will replace existing geometry on save
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button type="submit" loading={saving} disabled={!editingId && !drawnLayer}>
                  {editingId ? 'Update Asset' : 'Save Asset'}
                </Button>
                <Button type="button" variant="outline" onClick={closeForm} disabled={saving}>Cancel</Button>
              </div>
            </form>

            {/* Right: Leaflet draw map */}
            <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: 420 }}>
              <MapContainer center={[20, 75]} zoom={4} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
                />
                {assets.filter(hasRealGeometry).map((a) => (
                  <Polygon
                    key={a.id}
                    positions={a.geometry!.coordinates[0].map((pt) => [pt[1], pt[0]] as [number, number])}
                    pathOptions={{
                      color: a.id === editingId ? '#3b82f6' : '#9ca3af',
                      fillOpacity: a.id === editingId ? 0.15 : 0.08,
                      weight: a.id === editingId ? 2 : 1.5,
                      dashArray: a.id === editingId ? undefined : '4 4',
                    }}
                  />
                ))}
                <FeatureGroup>
                  <EditControl
                    position="topright"
                    onCreated={(e: any) => setDrawnLayer(e.layer)}
                    draw={{
                      rectangle:    false,
                      circle:       false,
                      circlemarker: false,
                      marker:       false,
                      polyline:     true,
                      polygon:      true,
                    }}
                    edit={{ edit: false, remove: false }}
                  />
                </FeatureGroup>
                {editingId && validAssets.filter((a) => a.id === editingId).length > 0 && (
                  <FitBounds assets={validAssets.filter((a) => a.id === editingId)} />
                )}
              </MapContainer>
            </div>
          </div>
        </Card>
      )}

      {/* ── List view ── */}
      {!showForm && viewMode === 'list' && (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {assets.length} asset{assets.length !== 1 ? 's' : ''} registered
              {assets.some((a) => !hasRealGeometry(a)) && (
                <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
                  — {assets.filter((a) => !hasRealGeometry(a)).length} missing boundary geometry (click ✏ to redraw)
                </span>
              )}
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-14"><Spinner size="lg" /></div>
          ) : assets.length === 0 ? (
            <div className="text-center py-14">
              <Building2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No assets registered</p>
              <p className="text-sm text-gray-400 mt-1 mb-4">Click <strong>Add Asset</strong> to draw the first infrastructure boundary</p>
              <Button onClick={openCreate}><PlusCircle className="w-4 h-4 mr-1" /> Add Asset</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Asset', 'Type', 'Project ID', 'Owner', 'Frequency', 'Area (ha)', 'Boundary', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {assets.map((a) => {
                    const geo = hasRealGeometry(a);
                    return (
                      <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{a.name}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 capitalize">
                          {a.metadata?.asset_type?.replace(/_/g, ' ') ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-400">
                          {a.metadata?.project_id ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{a.metadata?.owner ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 capitalize">{a.monitoring_frequency}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {a.area_hectares != null ? Number(a.area_hectares).toFixed(1) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {geo
                            ? <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400"><Check className="w-3 h-3" /> Set</span>
                            : <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">⚠ Missing — click ✏ to draw</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(a)}
                              className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-600" title="Edit asset">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(a.id, a.name)}
                              className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600" title="Delete asset">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Map view ── */}
      {!showForm && viewMode === 'map' && (
        <Card padding="none">
          <div style={{ height: 560 }} className="rounded-lg overflow-hidden">
            <MapContainer center={[20, 75]} zoom={4} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              {validAssets.length > 0 && <FitBounds assets={validAssets} />}
              {validAssets.map((a) => (
                <Polygon
                  key={a.id}
                  positions={a.geometry!.coordinates[0].map((pt) => [pt[1], pt[0]] as [number, number])}
                  pathOptions={{ color: '#3b82f6', weight: 2, fillOpacity: 0.2 }}
                >
                  <Popup>
                    <div className="space-y-1 text-sm min-w-[180px]">
                      <p className="font-semibold">{a.name}</p>
                      {a.metadata?.asset_type && (
                        <p className="text-xs text-gray-600 capitalize">🏗 {a.metadata.asset_type.replace(/_/g, ' ')}</p>
                      )}
                      {a.metadata?.project_id && <p className="text-xs text-gray-600">🔖 {a.metadata.project_id}</p>}
                      {a.metadata?.owner && <p className="text-xs text-gray-600">🏢 {a.metadata.owner}</p>}
                      {a.area_hectares != null && <p className="text-xs text-gray-600">📏 {Number(a.area_hectares).toFixed(1)} ha</p>}
                      <p className="text-xs text-gray-600">📡 Monitoring: {a.monitoring_frequency}</p>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => openEdit(a)} className="text-xs text-blue-600 underline">Edit</button>
                        <button onClick={() => handleDelete(a.id, a.name)} className="text-xs text-red-600 underline">Delete</button>
                      </div>
                    </div>
                  </Popup>
                </Polygon>
              ))}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-[1000]">
                  <Spinner />
                </div>
              )}
            </MapContainer>
          </div>
          {assets.some((a) => !hasRealGeometry(a)) && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-amber-600 dark:text-amber-400">
              ⚠ {assets.filter((a) => !hasRealGeometry(a)).length} asset(s) not shown — boundary geometry missing.
              Switch to <button className="underline" onClick={() => setViewMode('list')}>List view</button> to fix them.
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default AssetBoundariesPage;

// Made with Bob
